import { useFeatureFlagEnabled } from '@posthog/react';
import { useMemo } from 'react';
import { planStatusLabels } from '~/common/projectUtils';
import type { PlanStatus } from '~/common/types';
import { PROGRAM_MANAGER_FLAG } from '~/pages/common/NewProject/common';
import {
  getConstructionEndDateColumn,
  getPgeGasInformationColumns,
  getPgeRegionAndDivisionColumns,
  getProgramManagerColumns,
  getSceColumns,
  getSummaryColumns,
  type SummaryColumn,
  type SummaryColumnCallbacks,
} from './columns';
import { withFeatureChildCells } from './featureRows';

const PGE_PLAN_STATUS_LABELS: { [key in PlanStatus]: string } = {
  pending: 'Project Management Review',
  needsAttention: 'TBD',
  scheduled: 'Cleared',
} as { [key in PlanStatus]: string };

export interface SummaryColumnsConfig {
  showCharts: boolean;
  showGasInfo: boolean;
  columns: SummaryColumn[];
  statusLabels: { [key in PlanStatus]: string };
}

/**
 * Column set + labels for the org's conflictsSummaryConfig, mirroring the old
 * page's useSummaryConfig: pge adds Division/Region + Gas Information and
 * relabels plan statuses; sce appends its static columns; Construction End
 * Date is always inserted after the start date; the program-manager flag
 * inserts Program/Sub-Program Manager after PM.
 */
export function useSummaryColumns({
  conflictsSummaryConfig,
  callbacks,
}: {
  conflictsSummaryConfig?: string[];
  callbacks: SummaryColumnCallbacks;
}): SummaryColumnsConfig {
  const programManagerEnabled = useFeatureFlagEnabled(PROGRAM_MANAGER_FLAG);

  return useMemo(() => {
    const config: SummaryColumnsConfig = {
      showCharts: false,
      showGasInfo: false,
      columns: [],
      statusLabels: planStatusLabels,
    };

    if (conflictsSummaryConfig?.includes('pge')) {
      config.showCharts = true;
      config.showGasInfo = true;
      config.statusLabels = PGE_PLAN_STATUS_LABELS;
    }

    config.columns = getSummaryColumns({ statusLabels: config.statusLabels, callbacks });

    if (conflictsSummaryConfig?.includes('pge')) {
      config.columns.splice(2, 0, ...getPgeRegionAndDivisionColumns());
      config.columns.splice(config.columns.length - 1, 0, ...getPgeGasInformationColumns());
    }

    if (conflictsSummaryConfig?.includes('sce')) {
      config.columns.push(...getSceColumns());
    }

    const startIdx = config.columns.findIndex((c) => c.id === 'project.constructionStartDate');
    if (startIdx !== -1) {
      config.columns.splice(startIdx + 1, 0, getConstructionEndDateColumn(callbacks));
    }

    if (programManagerEnabled) {
      const pmIdx = config.columns.findIndex((c) => c.id === 'project.projectManager.name');
      if (pmIdx !== -1) {
        config.columns.splice(pmIdx + 1, 0, ...getProgramManagerColumns());
      }
    }

    // Accordion child-row cells wrap every leaf column of the final set.
    config.columns = withFeatureChildCells(config.columns);

    return config;
  }, [conflictsSummaryConfig, programManagerEnabled, callbacks]);
}
